import { format } from "date-fns";
import { Check, ChevronDownIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import "react-international-phone/style.css"; // Import the default styles for the phone input
import { PhoneNumberUtil } from "google-libphonenumber";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Webcam } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
  FormControl,
  FormLabel,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { CountryTypes, ErrorType, SourceTypes } from "@/types";
import profileimg from "@/assets/profile-image.svg";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  useGetAllSourceQuery,
  useGetCountriesQuery,
} from "@/services/commonApi";

import {
  useAddCoachMutation,
  useGetCoachCountQuery,
  useUpdateCoachMutation,
  useGetCoachesQuery,
  useGetCoachInSystemQuery,
} from "@/services/coachApi";

import { useGetAllMemberQuery, useGetMembersListQuery } from "@/services/memberApi";
import { UploadCognitoImage, deleteCognitoImage } from "@/lib/utils/s3Service";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { PhoneInput } from "react-international-phone";
import { RxCross2 } from "react-icons/rx";
import { formatNIC } from "@/lib/helper";
import { Gender, UserStatus } from "@/types/enums";
import { Coach } from "@/types/coach";
import { SearchComboBox } from "@/components/ui/globalsearch/search-combo-box";
import { User } from "@/types";
import { useGetSystemSettingsQuery } from "@/services/systemConfigApi";
import { Switch } from "@/components/ui/switch";
const { VITE_VIEW_S3_URL } = import.meta.env;

type RefetchType = ReturnType<typeof useGetCoachesQuery>["refetch"];

interface CoachFormProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  coachData: Coach | null;
  setCoachData: React.Dispatch<React.SetStateAction<Coach | null>>;
  refetch?: RefetchType;
}

const CoachForm: React.FC<CoachFormProps> = ({
  coachData,
  setCoachData,
  setOpen,
  open,
  refetch,
}) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const creator_id =
    useSelector((state: RootState) => state.auth.userInfo?.user?.id) || 0;

  const [country, setCountry] = useState(false);
  const [openSource, setOpenSource] = useState(false);

  const [dob, setDob] = useState(false);
  const membersSchema = z.number();
  const phoneUtil = PhoneNumberUtil.getInstance();

  const initialState = {
    profile_img: "",
    own_coach_id: "",
    first_name: "",
    last_name: "",
    gender: Gender.Male,
    dob: "",
    email: "",
    phone: "",
    mobile_number: "",
    notes: "",
    nic: "",
    source_id: 0,
    country_id: 0,
    city: "",
    coach_status: UserStatus.Pending,
    zipcode: "",
    address_1: "",
    address_2: "",
    is_trainer: false,
    org_id: orgId,
    created_by: creator_id,
    member_ids: [] as z.infer<typeof membersSchema>[],
  };

  const { data: otherSettingsData, refetch: settingsRefetch } =
    useGetSystemSettingsQuery({
      query: "groups=other_settings",
    });

  const other_settings = useMemo(
    () => otherSettingsData?.other_settings as Record<string, string>,
    [otherSettingsData]
  );

  const BankDetailsSchema = z
    .object({
      bank_name: z
        .string()
        .trim()
        .max(40, {
          message: "Should be 40 characters or less",
        })
        .optional(),
      iban_no: z
        .string()
        .trim()
        .max(34, {
          message: "Should be 34 characters or less",
        })
        .optional(),
      swift_code: z
        .string()
        .trim()
        .max(11, {
          message: "Should be 11 characters or less",
        })
        .optional(),
      acc_holder_name: z
        .string()
        .trim()
        .max(50, {
          message: "Should be 50 characters or less",
        })
        .optional(),
    })
    .optional();
  const FormSchema = z.object({
    profile_img: z.string().trim().default("").optional(),
    own_coach_id: z
      .string({
        required_error: "Required",
      })
      .trim()
      .min(1, { message: "Required" })
      .max(12, { message: "Cannot be greater then 12 characters" }),
    email: z
      .string()
      .min(8, { message: "Required" })
      .max(50, "Should be 50 characters or less")
      .refine(
        (value) =>
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(value),
        {
          message: "Incorrect email format",
        }
      ),
    first_name: z
      .string({
        required_error: "Required",
      })
      .trim()
      .max(40, "Sbould be 40 characters or less")
      .min(1, { message: "Required" })
      .refine((value) => /^[A-Za-z\-\s]+$/.test(value), {
        message: "Only alphabets, hyphen (-), and spaces are allowed",
      }),
    last_name: z
      .string({
        required_error: "Required",
      })
      .trim()
      .max(40, "Sbould be 40 characters or less")
      .min(1, { message: "Required" })
      .refine((value) => /^[A-Za-z\-\s]+$/.test(value), {
        message: "Only alphabets, hyphen (-), and spaces are allowed",
      }),
    gender: z.nativeEnum(Gender, {
      required_error: "You need to select a gender type.",
    }),
    dob: z.coerce
      .string({
        required_error: "Required",
      })
      .refine((value) => value.trim() !== "", {
        message: "Required",
      }),

    phone: z
      .string()
      .max(15, {
        message: "Cannot be greater than 15 characters",
      })
      .trim()
      .optional()
      .refine(
        (value) =>
          value === undefined || value === "" || /^\d{1,15}$/.test(value),
        {
          message: "Must be a number between 1 and 15 digits",
        }
      ),
    mobile_number: z
      .string()
      .max(20, {
        message: "Phone number cannot exceed 20 digits",
      })
      .trim()
      .optional()
      .refine(
        (value) => {
          if (!value) return true; // Skip validation if the field is optional and not provided
          if (value.length <= 4) {
            return true; // Pass validation if length is 5 or fewer
          }
          try {
            // Parse the phone number with the utility
            const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

            // Check if the parsed number is a valid phone number
            return phoneUtil.isValidNumber(parsedNumber);
          } catch (e) {
            // Return false if parsing fails (e.g., invalid format)
            return false;
          }
        },
        {
          message: "Invalid phone number", // Custom error message for refine validation
        }
      ),
    member_ids: z.array(membersSchema).optional(),
    coach_status: z.nativeEnum(UserStatus, {
      required_error: "You need to select a status type.",
    }),
    notes: z
      .string()
      .max(200, {
        message: "Notes must be greater than 200 characters",
      })
      .optional(),
    source_id: z.coerce
      .number({
        required_error: "Required",
      })
      .refine((value) => value !== 0, {
        message: "Required",
      }),
    address_1: z
      .string()
      .max(50, {
        message: "Address must be greater than 50 characters",
      })
      .optional(),
    address_2: z
      .string()
      .max(50, {
        message: "Address must be greater than 50 characters",
      })
      .optional(),
    zipcode: z
      .string()
      .trim()
      .max(15, {
        message: "Zipcode must be 15 characters or less",
      })
      .optional(),

    country_id: z.coerce
      .number({
        required_error: "Required",
      })
      .refine((value) => value !== 0, {
        message: "Required",
      }),
    is_trainer: z.boolean().default(false),
    city: z
      .string()
      .max(50, {
        message: "City be 50 characters or less",
      })
      .optional(),
    org_id: z
      .number({
        required_error: "Required",
      })
      .default(orgId),
    created_by: z
      .number({
        required_error: "Required",
      })
      .default(creator_id),
    nic: z
      .string()
      .optional() // Makes the field optional
      .refine((value) => !value || /^\d{5}-\d{7}-\d$/.test(value), {
        message: "CNIC must follow #####-#######-#",
      }),
    // bank_detail: BankDetailsSchema,
  });

  const orgName = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_name
  );
  const { data: coachCountData, refetch: refecthCount } = useGetCoachCountQuery(
    orgId,
    {
      skip: coachData != null,
    }
  );
  const [searchTerm, setSearchTerm] = React.useState("");


  const { data: users, isLoading: userSearchLoading } =
    useGetCoachInSystemQuery({
      query: searchTerm,
      limit: 10,
      offset: 0,
      sort_order: "desc",
      sort_key: "id",
      type: "coach",
    });

  const userOptions = useMemo(() => {
    return Array.isArray(users) ? users : [];
  }, [users]);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  const handleUserSelect = (selectedUser: User | null) => {
    console.log("Selected User:", selectedUser);

    if (!selectedUser) return; // Guard against null input

    const modifiedUser = {
      ...selectedUser,
      own_coach_id: watcher.own_coach_id,
      coach_status: UserStatus.Pending,
      member_ids: [],
      members: [],
      profile_img: selectedUser.profile_img ?? "",
      phone: selectedUser.phone ?? "",
      notes: selectedUser.notes ?? "",
      address_1: selectedUser.address_1 ?? "",
      address_2: selectedUser.address_2 ?? "",
      nic: selectedUser.nic ?? "",
      city: selectedUser.city ?? "",
      mobile_number:
        !selectedUser.mobile_number ||
          [0, 2, 3, 4].includes(selectedUser.mobile_number?.length)
          ? "+92"
          : selectedUser.mobile_number,
      org_id: orgId,
    };

    reset(modifiedUser);
  };

  const { data: countries } = useGetCountriesQuery();
  const { data: sources } = useGetAllSourceQuery();

  const [addCoach] = useAddCoachMutation();
  const [editCoach] = useUpdateCoachMutation();

  const {
    data: memberData,
  } = useGetAllMemberQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc" },
    {
      skip: !open
    }
  );

  const [selectedImage, setSelectedImage] = useState<File | null>(null);


  const [avatar, setAvatar] = useState<string | ArrayBuffer | null>();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    const maxSizeMB = 1;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file) {
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description:
            "Unsupported image type. Only PNG, JPG, and JPEG are supported.",
        });
        return;
      }

      if (file.size > maxSizeBytes) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description: `Image size exceeds ${maxSizeMB} MB limit.`,
        });
        return;
      }

      const reader = new FileReader();

      setSelectedImage(file);

      reader.onloadend = () => {
        setAvatar(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSetAvatarClick = () => {
    document.getElementById("fileInput")?.click();
  };

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: initialState,
    mode: "all",
  });

  const memberOptions = useMemo(() => {
    return Array.isArray(memberData?.data) ? memberData.data.map((member) => ({
      value: member.id,
      label: `${member.own_member_id} • ${member.first_name} ${member.last_name}`,
    })) : [];
  }, [memberData]);

  const {
    control,
    watch,
    register,
    setValue,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = form;

  const watcher = form.watch();

  const email = form.watch("email");

  function handleClose() {
    form.clearErrors();
    form.reset(initialState);
    setAvatar(null);
    setSelectedImage(null);
    setCoachData(null);
    setOpen(false);
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    let updatedData = {
      ...data,
      first_name: data.first_name.toLowerCase().trim(),
      last_name: data.last_name.toLowerCase().trim(),
      dob: format(new Date(data.dob!), "yyyy-MM-dd"),
      email: email.toLowerCase(),
      nic: data.nic?.length ? data.nic?.replace(/-/g, "") : undefined,
    };

    console.log("Updated data with only date:", updatedData);
    console.log("only once", data);
    if (selectedImage) {
      try {
        if (updatedData.profile_img !== "" && selectedImage) {
          await deleteCognitoImage(updatedData.profile_img as string);
        }
        const getUrl = await UploadCognitoImage(selectedImage);
        updatedData = {
          ...updatedData,
          profile_img: getUrl?.location as string,
        };
      } catch (error) {
        console.error("Upload failed:", error);
        toast({
          variant: "destructive",
          title: "Image Upload Failed",
          description: "Please try again.",
        });
        return;
      }
    }
    try {
      if (coachData == null) {
        const resp = await addCoach(updatedData).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Coach Created Successfully ",
          });
          refetch?.();
          refecthCount();
          handleClose();
        }
      } else {
        if (!coachData.id) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Coach ID is required for update",
          });
          return;
        }
        const resp = await editCoach({
          ...updatedData,
          id: coachData.id,
        }).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Coach Updated Successfully ",
          });
          refetch?.();
          handleClose();
        }
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
      refecthCount();
    }
  }

  useEffect(() => {
    if (!open) return;
    const total = coachCountData?.total_coaches as number;
    if (total >= 0) {
      form.setValue(
        "own_coach_id",
        `${other_settings?.coach_id_prefix ?? "AT-C"}${total + 1}`
      );
      // form.setValue("own_coach_id", `${orgName?.slice(0, 2)}-${total + 1}`);
      form.clearErrors();
    }
  }, [open, coachCountData]);

  useEffect(() => {
    if (!open || coachData == null) return;
    const payloadCoach = {
      ...coachData,
      member_ids: coachData?.members ?? [],
      nic: coachData?.nic !== null ? coachData?.nic : "",
    };
    console.log("Member_ids before that", payloadCoach.member_ids);

    if (
      payloadCoach?.mobile_number &&
      [0, 2, 3, 4].includes(payloadCoach?.mobile_number?.length)
    ) {
      payloadCoach.mobile_number = `+92`;
    } else {
      payloadCoach.mobile_number = payloadCoach?.mobile_number; // keep it as is
    }
    console.log("Member_ids", payloadCoach.member_ids);
    form.reset(payloadCoach);
    if (payloadCoach?.nic?.length) {
      setValue("nic", formatNIC(payloadCoach?.nic) ?? "");
    }
    // setAvatar(coachData?.profile_img as string);
  }, [open, coachData]);


  const onError = () => {
    console.log(errors);
    const allRequired = Object.values(errors).every(
      (error) => error?.message === "Required"
    );

    console.log("all required", allRequired, errors);

    if (allRequired) {
      toast({
        variant: "destructive",
        description: "Please fill all the mandatory fields",
      });
    } else {
      toast({
        variant: "destructive",
        description: "Fix the errors and try again",
      });
    }
  };
  console.log({ watcher, errors });
  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1300px] py-0 custom-scrollbar"
      >
        <Form {...form}>
          <form noValidate onSubmit={form.handleSubmit(onSubmit, onError)}>
            <SheetHeader className="sticky !top-0 z-40 py-4 bg-white">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-start  bg-white">
                  <div>
                    <p className="font-semibold">
                      {coachData == null ? "Add" : "Edit"} Coach
                    </p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        Dashboard
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {coachData == null ? "Add" : "Edit"} Coach
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <div>
                      <Button
                        type={"button"}
                        onClick={handleClose}
                        className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                      >
                        <RxCross2 className="w-4 h-4" /> Cancel
                      </Button>
                    </div>
                    <div>
                      <LoadingButton
                        type="submit"
                        className="w-[120px] bg-primary text-black text-center flex items-center gap-2"
                        loading={form.formState.isSubmitting}
                        disabled={form.formState.isSubmitting}
                      >
                        {!form.formState.isSubmitting && (
                          <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                        )}
                        {coachData ? "Update" : "Save"}
                      </LoadingButton>
                    </div>
                  </div>
                </div>
              </SheetTitle>
              <Separator className=" h-[1px] rounded-full my-2" />
            </SheetHeader>
            <SheetDescription className="pb-4">
              <div className="flex justify-between items-center">
                <div className="flex flex-row gap-4 items-center">
                  <div className="relative flex">
                    <img
                      id="avatar"
                      src={
                        avatar
                          ? String(avatar)
                          : watcher.profile_img
                            ? watcher.profile_img.includes('https://')
                              ? watcher.profile_img
                              : `${VITE_VIEW_S3_URL}/${watcher.profile_img}`
                            : profileimg
                      }
                      alt={profileimg}
                      className="w-14 h-14 rounded-full object-cover mb-4 relative"
                    />
                  </div>
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <Button
                    onClick={handleSetAvatarClick}
                    variant={"outline"}
                    type="button"
                    className="px-4 py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-white"
                  >
                    <Webcam className="h-4 w-4" />
                    Profile Snapshot
                  </Button>
                </div>
                <div>
                  <SearchComboBox
                    items={userOptions || []}
                    onSearchChange={handleSearchChange}
                    onItemSelect={handleUserSelect}
                    placeholder="Start typing to search users..."
                    className="min-w-[400px]"
                    loading={userSearchLoading}
                    getItemKey={(user) => user.id}
                    getItemLabel={(user) =>
                      `${user.first_name} ${user.last_name}`
                    }
                    capitalize={true}
                    renderItem={(user) => (
                      <div className="space-y-1">
                        <div className="flex gap-2 items-center">
                          <span className="capitalize">{`${user.first_name} ${user.last_name}`}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {user.email}
                        </div>
                      </div>
                    )}
                    emptyMessage="No users found matching your search"
                    debounceMs={500}
                  />
                </div>
              </div>
              <div>
                <h1 className="font-bold text-lg mb-2 text-gray-900">
                  Basic Information
                </h1>
              </div>
              <div className="w-full grid grid-cols-3 gap-3 justify-between items-center">
                <div className="relative">
                  <FormField
                    control={form.control}
                    name="own_coach_id"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="own_coach_id"
                          className="disabled:!opacity-100 disabled:text-gray-800 placeholder:text-gray-800"
                          label="Coach Id"
                          maxLength={12}
                          min={1}
                          text="*"
                          required={true}
                          onChange={(e) => {
                            // Prevent leading and trailing spaces in real time
                            const trimmedValue = e.target.value.replace(
                              /^\s+|\s+$/g,
                              ""
                            );
                            field.onChange(trimmedValue); // Update field value
                          }}
                        // disabled
                        />
                        {watcher.own_coach_id ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        {coachData == null ||
                          (coachData != null &&
                            watcher.coach_status == "pending") ? (
                          <FloatingLabelInput
                            {...field}
                            id="email"
                            text="*"
                            label="Email Address"
                          />
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <FloatingLabelInput
                                  {...field}
                                  id="email"
                                  label="Email Address"
                                  text="*"
                                  disabled={
                                    coachData != null &&
                                    watcher.coach_status != "pending"
                                  }
                                />
                              </TooltipTrigger>

                              <TooltipContent>
                                You cannot update the email address once the
                                coach is active
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {<FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="first_name"
                          label="First Name"
                          text="*"
                          className="capitalize"
                        />
                        <FormMessage>
                          {form.formState.errors.first_name?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="last_name"
                          label="Last Name"
                          text="*"
                          className="capitalize"
                        />
                        <FormMessage>
                          {form.formState.errors.last_name?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          onValueChange={(value: Gender) =>
                            form.setValue("gender", value)
                          }
                          value={field.value as Gender}
                        >
                          <FormControl>
                            <SelectTrigger
                              floatingLabel="Gender"
                              text="*"
                              className={`${watcher.gender ? "text-black" : ""}`}
                            >
                              <SelectValue placeholder="Select Gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                            <SelectItem value="prefer not to say">
                              Prefer not to say
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {watcher.gender ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="dob"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Popover open={dob} onOpenChange={setDob}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute p-0 text-xs left-2 -top-1.5 px-1 bg-white">
                                  Date of brith{" "}
                                  <span className="text-red-500">*</span>
                                </span>

                                <Button
                                  variant={"outline"}
                                  type="button"
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "dd-MM-yyyy")
                                  ) : (
                                    <span className="font-normal text-gray-400">
                                      Select date of birth
                                    </span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </div>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="center">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown-buttons"
                              selected={new Date(field.value)}
                              onSelect={(selectedDate) => {
                                if (selectedDate) {
                                  const today = new Date();
                                  const oneYearAgo = new Date();
                                  oneYearAgo.setFullYear(
                                    today.getFullYear() - 1
                                  );

                                  // Check if the selected date is at least 1 year old
                                  if (selectedDate <= oneYearAgo) {
                                    field.onChange(selectedDate);
                                    setDob(false);
                                  } else {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Date of birth must be at least 1 year old.",
                                    });
                                  }
                                }
                              }}
                              fromYear={1950}
                              toYear={new Date().getFullYear()}
                              defaultMonth={
                                new Date(
                                  field && field.value
                                    ? field.value
                                    : Date.now()
                                )
                              }
                              disabled={(date: Date) =>
                                date > new Date() ||
                                date < new Date("1900-01-01")
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        {watcher.dob ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>

                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="phone"
                          label="Landline Number"
                        />
                        {<FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="mobile_number"
                    render={({ field }) => (
                      <FormItem>
                        <div className="relative ">
                          <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-[60]">
                            Phone Number
                          </span>
                          <PhoneInput
                            defaultCountry="pk"
                            value={field.value}
                            // forceDialCode={true}
                            onChange={field.onChange}
                            inputClassName="w-full "
                          />
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="member_ids"
                    render={({ field: { onChange } }) => (
                      <FormItem className="w-full ">
                        <MultiSelect
                          floatingLabel={"Assign Members"}
                          // disabled={watcher.is_trainer}
                          options={memberOptions}
                          defaultValue={form.watch("member_ids") || []} // Ensure defaultValue is always an array
                          onValueChange={(selectedValues) => {
                            console.log("Selected Values: ", selectedValues); // Debugging step
                            onChange(selectedValues); // Pass selected values to state handler
                          }}
                          placeholder={"Select members"}
                          variant="inverted"
                          maxCount={1}
                          className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 "
                        />

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative">
                  <FormField
                    control={form.control}
                    name="coach_status"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          disabled={field.value == "pending"}
                          onValueChange={(value: UserStatus) =>
                            form.setValue("coach_status", value)
                          }
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              floatingLabel="Status"
                              text="*"
                              className={"font-medium text-gray-400"}
                            >
                              <SelectValue placeholder="Select Coach status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem
                              value="pending"
                              className={`${coachData && "hidden"}`}
                            >
                              Pending
                            </SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        {watcher.coach_status ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative">
                  <FormField
                    control={form.control}
                    rules={{
                      pattern: {
                        value: /^\d{5}-\d{7}-\d$/,
                        message: "CNIC must follow #####-#######-#",
                      },
                    }}
                    name="nic"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="nic"
                          label="CNIC"
                          value={field.value ? String(field.value) : ""} // Ensure value is a string or empty
                          onChange={(e) => {
                            const formattedValue = formatNIC(e.target.value);
                            field.onChange(formattedValue || ""); // Default to empty string
                          }}
                        />
                        {<FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>

                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="source_id"
                    render={({ field }) => (
                      <FormItem>
                        <div className="relative">
                          <label
                            className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${field.value
                              ? "text-xs -top-2.5"
                              : "text-xs text-black"
                              }`}
                          >
                            Source <span className="text-red-500">*</span>
                          </label>
                          <Popover
                            open={openSource}
                            onOpenChange={setOpenSource}
                          >
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className="capitalize w-full hover:bg-transparent border-[1px] shadow-sm"
                                >
                                  <span className="w-full text-left font-normal text-black">
                                    {field.value
                                      ? sources?.find(
                                        (source) => source.id === field.value
                                      )?.source
                                      : "Select Source"}
                                  </span>
                                  <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative "
                              side="bottom"
                            >
                              <Command className="w-full ">
                                <CommandList>
                                  <CommandInput placeholder="Select Source" />
                                  <CommandEmpty>
                                    No Source List found.
                                  </CommandEmpty>
                                  <CommandGroup className="">
                                    {sources &&
                                      sources.map((sources: SourceTypes) => (
                                        <CommandItem
                                          value={sources.source}
                                          key={sources.id}
                                          className="capitalize"
                                          onSelect={() => {
                                            setValue("source_id", sources.id);
                                            setOpenSource(false);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              sources.id == field.value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {sources.source}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        {watcher.source_id ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>

                <div className="relative ">
                  <FormField
                    control={form.control}
                    rules={{
                      maxLength: {
                        value: 200,
                        message: "Notes should not exceed 350 characters",
                      },
                    }}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="notes"
                          type="textarea"
                          rows={3}
                          maxLength={200}
                          label="Notes"
                        />
                        {watcher.notes ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    rules={{
                      required: "Required",
                    }}
                    name="is_trainer"
                    render={({ field }) => (
                      <FormItem className="flex gap-2 items-center justify-start">
                        <FormLabel className="text-base">
                          Trainer
                        </FormLabel>
                        <Switch
                          checked={field.value as boolean}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            // if (checked) {
                            //   form.setValue("member_ids", []);
                            // }
                          }}
                        />
                        {watcher.is_trainer ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div>
                <h1 className="font-bold text-lg text-gray-900 my-2">
                  Address
                </h1>
              </div>
              <div className="w-full grid grid-cols-3 gap-3 justify-between items-center">
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="address_1"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="address_1"
                          label="Street Address"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="address_2"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="address_2"
                          label="Extra Address"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="zipcode"
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput
                          {...field}
                          id="zipcode"
                          label="Zip Code"
                        />
                        <FormMessage className="" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative">
                  <FormField
                    control={form.control}
                    name="country_id"
                    render={({ field }) => (
                      <FormItem className="flex flex-col w-full">
                        <div className="flex flex-col w-full">
                          <label
                            className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${field.value
                              ? "text-xs -top-2.5"
                              : "text-xs text-black"
                              }`}
                          >
                            Country <span className="text-red-500">*</span>
                          </label>
                          <Popover open={country} onOpenChange={setCountry}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    "font-normal text-gray-800 border-[1px] justify-between hover:bg-transparent hover:text-gray-800",
                                    !field.value && "  focus:border-primary "
                                  )}
                                >
                                  {field.value
                                    ? countries?.find(
                                      (country: CountryTypes) =>
                                        country.id === field.value // Compare with numeric value
                                    )?.country // Display country name if selected
                                    : "Select country"}
                                  <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="p-0">
                              <Command>
                                <CommandList>
                                  <CommandInput placeholder="Select Country" />
                                  <CommandEmpty>No country found.</CommandEmpty>
                                  <CommandGroup>
                                    {countries &&
                                      countries.map((country: CountryTypes) => (
                                        <CommandItem
                                          value={country.country}
                                          key={country.id}
                                          onSelect={() => {
                                            form.setValue(
                                              "country_id",
                                              country.id // Set country_id to country.id as number
                                            );
                                            setCountry(false);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              country.id === field.value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {country.country}{" "}
                                          {/* Display the country name */}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>

                        {watcher.country_id ? <></> : <FormMessage />}
                      </FormItem>
                    )}
                  />
                </div>
                <div className="relative ">
                  <FormField
                    control={form.control}
                    name="city"
                    rules={{
                      maxLength: {
                        value: 50,
                        message: "City should be less than 50 character.",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FloatingLabelInput {...field} id="city" label="City" />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </SheetDescription>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default CoachForm;
